import { useThree, ThreeEvent } from '@react-three/fiber';
import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useAppStore, useTool } from '../state/store';
import {
    SelectionManager,
    useSelectionStore,
    getSelectionAction,
    applySelectionAction,
    type EntityBounds,
} from '../tools/selection';
import { entityMeshRegistry } from './toolEventBus';
import { resolveVisibility, currentVisibilityInput } from './visibility';
import { useDocumentStore } from '../state/useDocumentStore';
import { useSelCycleStore } from '../state/useSelCycleStore';
// Direct import: the snapping barrel also exports the drei-based
// SnapIndicator, which must not join the scene-graph chunk.
import { useSnapStore } from '../tools/snapping/SnapManager';

// Decomposed layers (Phase 2) + shared interaction plane
import { EntityLayer } from './EntityLayer';
import { BIMShadedLayer } from './BIMShadedLayer';
import { WallHoverControlLayer } from './WallHoverControlLayer';
import { SystemOverlayLayer } from './SystemOverlayLayer';
import { ToolScope } from './ToolScope';
import { PaperSpaceCadLayer } from './PaperSpaceCadLayer';
import { useLayoutStore } from '../state/useLayoutStore';
import { NurbsSelectionBridge } from './NurbsSelectionBridge';
import { GripEditLayer } from './GripEditLayer';
import { ConstraintGlyphLayer } from './ConstraintGlyphLayer';
import { ImportedCadLayer } from './ImportedCadLayer';
import { BimDrawingUnderlay } from './BimDrawingUnderlay';
import { BimModelReferenceLayer } from './BimModelReferenceLayer';
import { setEntityClickRouter } from './toolEventBus';
import { CommandInteractScope } from './CommandInteractScope';
import { DynamicInputBridge } from './DynamicInputBridge';
import { GumballLayer } from './GumballLayer';
import { SketchReferenceLayer } from './SketchReferenceLayer';
import { useCommandInteract } from '../core/commands/commandInteract';
import { gumballOwnsPointer } from '../state/useGumballStore';
import { getEntityIndex } from '../core/spatial/entityIndex';
import type { AABB } from '../core/spatial/quadtree';

/**
 * Faz 10.2: world-XY rect for a screen-space selection shape, resolved by
 * unprojecting the shape's points. Exact under the 2D workbench's top-down
 * ortho camera — the 50k-entity drafting target — where the rays run
 * parallel to Z so every unprojected x/y is its own z=0 plane hit. Any
 * other projection returns null: for perspective or tilted cameras a
 * plane section is NOT a conservative bound (geometry above/below the
 * plane projects into the box from outside it), so the pre-filter must be
 * skipped rather than risk dropping real hits.
 */
function selectionWorldRect(
    screenPoints: THREE.Vector2[],
    camera: THREE.Camera,
    gl: THREE.WebGLRenderer
): AABB | null {
    if (!(camera instanceof THREE.OrthographicCamera) || screenPoints.length < 2) return null;
    const dir = new THREE.Vector3();
    camera.getWorldDirection(dir);
    if (Math.abs(dir.z) < 0.999) return null;
    const size = gl.getSize(new THREE.Vector2());
    if (size.x <= 0 || size.y <= 0) return null;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const p of screenPoints) {
        const ndc = new THREE.Vector3(
            (p.x / size.x) * 2 - 1,
            -((p.y / size.y) * 2 - 1),
            0
        ).unproject(camera);
        if (!Number.isFinite(ndc.x) || !Number.isFinite(ndc.y)) return null;
        if (ndc.x < minX) minX = ndc.x;
        if (ndc.y < minY) minY = ndc.y;
        if (ndc.x > maxX) maxX = ndc.x;
        if (ndc.y > maxY) maxY = ndc.y;
    }
    return [minX, minY, maxX, maxY];
}

/**
 * Enhanced Scene Content Component
 *
 * Phase 2 decomposition: this component is now a thin composition root.
 * - EntityLayer    -> CAD entities (memoized per-element, instanced bulk)
 * - BIMShadedLayer -> kernel BIM elements through the same shaded renderer
 *   the BIM workbench uses (one display language on every workbench)
 * - ToolScope      -> only the active tool's hook + preview mounts (2.4)
 * - SystemOverlayLayer -> snap/selection-box/transform overlays (memoized)
 *
 * `cad2D` (2D workbench): 2D-only lightweight scene — BIM overlay and
 * 3D/BIM/NURBS entities are skipped; primitives render as wireframe.
 */
export function SceneContent({ cad2D = false }: { cad2D?: boolean }) {
    const tool = useTool();
    // CAD paper space (Faz 9, option A): when a layout is active in the
    // 2D workspace, the model is framed through MVIEW viewports.
    const activeLayoutId = useLayoutStore((s) => (cad2D ? s.activeId : null));
    // Hybrid 2D drawings: when the 2D workbench edits a drawing view, only
    // that view's tagged entities render, above the BIM-derived underlay.
    const activeDrawingViewId = useAppStore((s) => (cad2D ? s.activeDrawingViewId : null));

    const { camera, gl, scene } = useThree();
    const selectionManagerRef = useRef<SelectionManager | null>(null);

    useEffect(() => {
        selectionManagerRef.current = new SelectionManager(camera, gl, scene);
    }, [camera, gl, scene]);

    // Box-selection drag state (screen-space rectangle)
    const wasSelectingRef = useRef(false);
    const lastPointerEventRef = useRef<PointerEvent | null>(null);
    const isBoxSelecting = useSelectionStore((s) => s.isSelecting);
    const select = useAppStore((s) => s.select);
    const addToSelection = useAppStore((s) => s.addToSelection);
    const removeFromSelection = useAppStore((s) => s.removeFromSelection);

    // When a box drag finishes, resolve the entity ids inside the box and
    // apply them to the app selection store (window/crossing + modifiers).
    useEffect(() => {
        if (isBoxSelecting) {
            wasSelectingRef.current = true;
            return;
        }
        if (!wasSelectingRef.current) return;
        wasSelectingRef.current = false;

        const box = useSelectionStore.getState().selectionBox;
        if (box && selectionManagerRef.current) {
            // A4: box-select resolves membership through the shared document
            // ledger + VisibilityResolver, so CAD entities and BIM overlay
            // objects obey the same visibility/lock language here. Objects
            // hidden (per-object, layer, or view scope) or locked are not
            // selectable; layer metadata comes from the ledger when present.
            const ledger = useDocumentStore.getState().sceneObjects;
            const visInput = currentVisibilityInput();
            // Quadtree pre-filter (Faz 10.2): under the top-down ortho camera
            // the screen box maps to an exact world rect, and only the index
            // candidates get their scene bounds built — a 50k-entity box
            // select stops walking the whole mesh registry per drag.
            const worldRect = selectionWorldRect(
                [box.start, box.end], camera, gl
            );
            const candidates = worldRect
                ? new Set(getEntityIndex().queryRect(worldRect))
                : null;
            const entityBounds = new Map<string, EntityBounds>();
            entityMeshRegistry.forEach((obj, id) => {
                if (candidates && !candidates.has(id)) return;
                const sceneObj = ledger[id];
                if (sceneObj) {
                    const r = resolveVisibility(sceneObj, visInput);
                    if (!r.visible || r.locked) return;
                }
                const bounds = new THREE.Box3().setFromObject(obj);
                if (bounds.isEmpty()) return;
                entityBounds.set(id, {
                    id,
                    type: sceneObj?.source ?? String(obj.userData.entityType ?? ''),
                    layer: sceneObj?.layerId ?? String(obj.userData.layer ?? ''),
                    visible: true,
                    locked: false,
                    worldBounds: bounds,
                });
            });

            const found = selectionManagerRef.current.findEntitiesInBox(
                box,
                entityBounds,
                useSelectionStore.getState().filters
            );
            // Expand grouped hits to their full group (same rule as click).
            const appForGroups = useAppStore.getState();
            const expanded = new Set(found);
            found.forEach((id) => {
                const gid = appForGroups.entities[id]?.data?.groupId as string | undefined;
                if (gid) {
                    Object.values(appForGroups.entities).forEach((e) => {
                        if (e.data?.groupId === gid) expanded.add(e.id);
                    });
                }
            });
            const current = useAppStore.getState().selectedIds;
            const action = getSelectionAction(
                lastPointerEventRef.current ?? new PointerEvent('pointerup')
            );
            useAppStore.setState({
                selectedIds: applySelectionAction(current, Array.from(expanded), action),
            });
        }
        useSelectionStore.getState().cancelSelection();
    }, [isBoxSelecting]);

    // Publish selection routing for isolated layers (EntityLayer, BIM overlays)
    useEffect(() => {
        setEntityClickRouter((entityId, event) => {
            if (tool === 'select') {
                // Unified selection contract (shared with box select via
                // getSelectionAction): Shift = add, Ctrl = remove.
                const app = useAppStore.getState();
                // Selection cycling: the click's ray may pierce several
                // stacked entities; each plain click advances to the next
                // one under the cursor (classic stacked-pick cycling).
                const stacked: string[] = [];
                for (const hit of event.intersections ?? []) {
                    let obj: THREE.Object3D | null = hit.object;
                    while (obj) {
                        const id = obj.userData?.entityId as string | undefined;
                        if (id && !stacked.includes(id)) stacked.push(id);
                        obj = obj.parent;
                    }
                }
                const current = app.selectedIds;
                const alreadyOn = stacked.length > 1 && current.length === 1 && stacked.includes(current[0]);
                // SELECTIONCYCLING popup (Faz 4.4): with the display mode on,
                // a stacked pick opens the candidate list (type/layer/color
                // rows) instead of silently advancing — modifiers keep their
                // add/remove semantics and skip the popup.
                if (
                    stacked.length >= 2
                    && useSnapStore.getState().selectionCycling
                    && !event.shiftKey && !event.ctrlKey && !event.altKey
                ) {
                    const ledger = useDocumentStore.getState().sceneObjects;
                    const layers = useAppStore.getState().layers;
                    const candidates = stacked.map((id) => {
                        const e = app.entities[id];
                        const layerId = e?.layer ?? ledger[id]?.layerId ?? '';
                        return {
                            id,
                            type: e?.type ?? 'object',
                            layerName: layers[layerId]?.name ?? layerId,
                            color: e?.data?.color as string | undefined,
                        };
                    });
                    useSelCycleStore.getState().open(
                        event.nativeEvent.clientX,
                        event.nativeEvent.clientY,
                        candidates
                    );
                    return;
                }
                if (alreadyOn && !event.shiftKey && !event.ctrlKey && !event.altKey) {
                    const pos = stacked.indexOf(current[0]);
                    app.select([stacked[(pos + 1) % stacked.length]]);
                    return;
                }
                // 2D grouping: clicking a grouped object selects the
                // whole group (GROUP tags data.groupId, UNGROUP clears it).
                const groupId = app.entities[entityId]?.data?.groupId as string | undefined;
                const members = groupId
                    ? Object.values(app.entities).filter((e) => e.data?.groupId === groupId).map((e) => e.id)
                    : [entityId];
                if (event.shiftKey) {
                    addToSelection(members);
                } else if (event.ctrlKey) {
                    removeFromSelection(members);
                } else {
                    select(members);
                }
            }
        });
        return () => setEntityClickRouter(null);
    }, [tool, select, addToSelection, removeFromSelection]);

    const screenPointOf = (event: ThreeEvent<PointerEvent>): THREE.Vector2 => {
        const native = event.nativeEvent;
        return new THREE.Vector2(native.offsetX, native.offsetY);
    };

    const hitEntity = (event: ThreeEvent<PointerEvent>): boolean => {
        const first = event.intersections[0];
        if (!first) return false;
        if (first.instanceId != null) return true;
        let obj: THREE.Object3D | null = first.object;
        while (obj) {
            if (obj.userData?.entityId) return true;
            obj = obj.parent;
        }
        return false;
    };

    // A stationary press gives the gizmo no hover beforehand: Transform-
    // Controls establishes its axis on pointerdown, after this dispatch, so
    // the ownership check re-runs one tick after arming — a press that
    // started a gizmo drag cancels the box/lasso instead of dragging both.
    const standBoxDownOnGizmoGrab = (): void => {
        window.setTimeout(() => {
            if (gumballOwnsPointer()) useSelectionStore.getState().cancelSelection();
        }, 0);
    };

    const handlePointerDown = (event: ThreeEvent<PointerEvent>) => {
        lastPointerEventRef.current = event.nativeEvent;
        // A running command interaction owns the pointer while active.
        if (useCommandInteract.getState().active) return;
        if (tool !== 'select') return;
        // Left button only: middle drag is camera pan (OrbitControls) and
        // must never start a window/crossing selection.
        if (event.button !== 0) return;
        if (useAppStore.getState().isViewCubeHovered) return;
        // The gizmo owns the pointer on/around its handles: a press that
        // grabs a handle must never start a window/lasso selection.
        if (gumballOwnsPointer()) return;
        const sel = useSelectionStore.getState();
        // Alt + left drag on empty ground = lasso (freehand polygon selection).
        if (event.nativeEvent.altKey) {
            if (hitEntity(event)) return;
            sel.setMode('lasso');
            sel.startSelection(screenPointOf(event), 'window');
            sel.addLassoPoint(screenPointOf(event));
            standBoxDownOnGizmoGrab();
            return;
        }
        if (hitEntity(event)) return;
        sel.setMode('window');
        sel.startSelection(screenPointOf(event), 'window');
        standBoxDownOnGizmoGrab();
    };

    const handlePointerMove = (event: ThreeEvent<PointerEvent>) => {
        lastPointerEventRef.current = event.nativeEvent;
        const sel = useSelectionStore.getState();
        if (!sel.isSelecting) return;
        if (sel.mode === 'lasso') sel.addLassoPoint(screenPointOf(event));
        else sel.updateSelection(screenPointOf(event));
    };

    const handlePointerUp = (event: ThreeEvent<PointerEvent>) => {
        lastPointerEventRef.current = event.nativeEvent;
        const sel = useSelectionStore.getState();
        if (!sel.isSelecting) return;
        sel.endSelection();
        // Lasso release: resolve membership through the polygon finder, then
        // clean the lasso state. The box path resolves in the effect above.
        if (sel.mode === 'lasso') {
            const lasso = useSelectionStore.getState().lassoPoints;
            if (lasso.length >= 3 && selectionManagerRef.current) {
                const ledger = useDocumentStore.getState().sceneObjects;
                const visInput = currentVisibilityInput();
                // Quadtree pre-filter (Faz 10.2), same contract as box-select.
                const worldRect = selectionWorldRect(lasso, camera, gl);
                const candidates = worldRect
                    ? new Set(getEntityIndex().queryRect(worldRect))
                    : null;
                const entityBounds = new Map<string, EntityBounds>();
                entityMeshRegistry.forEach((obj, id) => {
                    if (candidates && !candidates.has(id)) return;
                    const sceneObj = ledger[id];
                    if (sceneObj) {
                        const r = resolveVisibility(sceneObj, visInput);
                        if (!r.visible || r.locked) return;
                    }
                    const bounds = new THREE.Box3().setFromObject(obj);
                    if (bounds.isEmpty()) return;
                    entityBounds.set(id, {
                        id,
                        type: sceneObj?.source ?? String(obj.userData.entityType ?? ''),
                        layer: sceneObj?.layerId ?? String(obj.userData.layer ?? ''),
                        visible: true,
                        locked: false,
                        worldBounds: bounds,
                    });
                });
                const found = selectionManagerRef.current.findEntitiesInPolygon(
                    lasso, entityBounds, useSelectionStore.getState().filters
                );
                const current = useAppStore.getState().selectedIds;
                const action = getSelectionAction(event.nativeEvent);
                useAppStore.setState({
                    selectedIds: applySelectionAction(current, found, action),
                });
            }
            useSelectionStore.setState({ mode: 'click' });
            useSelectionStore.getState().cancelSelection();
        }
    };

    return (
        <group onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerUp}>
            <CommandInteractScope />
            <DynamicInputBridge />
            {!cad2D && <GumballLayer />}
            <ToolScope>
                {cad2D && activeLayoutId
                    ? <PaperSpaceCadLayer />
                    : <EntityLayer cad2D={cad2D} viewId={activeDrawingViewId} />}
                {cad2D && activeDrawingViewId && !activeLayoutId && (
                    <BimDrawingUnderlay viewId={activeDrawingViewId} />
                )}
                {!cad2D && <BIMShadedLayer />}
                {/* Sketch solids drawn at the Sketch workbench stay live and
                    selectable in every shared viewport (B4 edit bridge); in
                    3D they render as shaded face meshes like in Sketch, in
                    2D as drafting wireframe references. */}
                <SketchReferenceLayer shaded={!cad2D} />
                {/* BIM model elements as live wireframe references in the 2D
                    workbench's model space: selectable/erasable/movable
                    through the edit router (drawing-view underlays keep
                    their read-only semantics). */}
                {cad2D && !activeLayoutId && !activeDrawingViewId && <BimModelReferenceLayer />}
                {cad2D && !activeLayoutId && <ImportedCadLayer />}
                {/* Parametric constraint glyphs (Faz 6.7): model-space only;
                    paper-space layouts keep the sheet clean. */}
                {cad2D && !activeLayoutId && <ConstraintGlyphLayer />}
                <NurbsSelectionBridge />
                {!(cad2D && activeLayoutId) && <GripEditLayer />}
                {/* BIM wall hover drag control (extend from ends /
                    junctions): walls render through BIMShadedLayer in the
                    3D model space of this workbench. */}
                {!cad2D && <WallHoverControlLayer />}
                <SystemOverlayLayer />
            </ToolScope>
        </group>
    );
}
